import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FeatherModule } from 'angular-feather';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CompareServiceService } from 'src/app/customer/cf-customer/cf-comparison/service/compare-service.service';
import { TfService } from 'src/app/tf/tf-service.service';
import { formatProductCardCurrencyDisplayUnit } from 'src/app/Theme/shared/components/product/product-card-currency-format';
import { ProductCardDisplayConfigService } from 'src/app/Theme/shared/components/product/product-card-display-config.service';
import { ProductCardLayoutType } from 'src/app/Theme/shared/components/product/product-card-display.model';
import { isProductCardElementVisible } from 'src/app/Theme/shared/components/product/product-card-display.utils';

@Component({
  selector: 'app-compare-navbar',
  standalone: true,
  imports: [CommonModule, TranslateModule, FeatherModule],
  templateUrl: './compare-navbar.component.html',
  styleUrl: './compare-navbar.component.scss'
})
export class CompareNavbarComponent implements OnInit, OnDestroy {
  compareProducts: any[] = [];
  emptySlots: number[] = [];
  remainingSlots = 0;
  showCompareTray = false;
  showSelectedList = false;

  @HostBinding('class.compare-navbar-host--selected')
  get selectedListOpen(): boolean {
    return this.showSelectedList;
  }
  private isProductCardPage = false;
  /** True after leaving a product page, until the next visit clears the compare slots. */
  private leftProductPages = false;
  private isCustomerLoggedIn = false;
  private compareButtonOnCard = false;
  private subscriptions = new Subscription();

  constructor(
    private router: Router,
    private compareService: CompareServiceService,
    private tfService: TfService,
    private productCardDisplayConfig: ProductCardDisplayConfigService,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    document.body.appendChild(this.host.nativeElement);
    this.updateRoute(this.router.url);
    this.productCardDisplayConfig.ensureLoaded();
    this.subscriptions.add(
      this.tfService.authInfo$.subscribe((auth) => {
        this.isCustomerLoggedIn = !!auth?.isAuthenticated;
        this.updateCompareTrayVisibility();
        this.cdr.detectChanges();
      })
    );
    this.subscriptions.add(
      this.productCardDisplayConfig.displayConfigs$.subscribe(() => {
        this.syncCompareButtonVisibility();
        this.updateCompareTrayVisibility();
        this.cdr.detectChanges();
      })
    );
    this.subscriptions.add(
      this.compareService.products$.subscribe((products) => {
        this.compareProducts = products || [];
        this.remainingSlots = Math.max(0, this.compareService.maxProducts - this.compareProducts.length);
        this.emptySlots = Array.from({ length: this.remainingSlots }, (_, i) => i);
        if (!this.compareProducts.length) {
          this.showSelectedList = false;
        }
        this.updateCompareTrayVisibility();
        this.cdr.detectChanges();
      })
    );
    this.subscriptions.add(
      this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event) => {
          this.onNavigation(event.urlAfterRedirects);
          this.cdr.detectChanges();
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.host.nativeElement.remove();
  }

  getDefaultImage(product: any): string {
    if (!product?.Images?.length) {
      return 'assets/images/default.jpg';
    }
    const defaultImg = product.Images.find((img: any) => img.default_image === true);
    return defaultImg?.image_preview || product.Images[0]?.image_preview || 'assets/images/default.jpg';
  }

  removeFromCompare(event: Event, product: any): void {
    event.preventDefault();
    event.stopPropagation();
    const itemCode = this.compareService.getItemCode(product);
    if (itemCode == null) {
      return;
    }
    this.compareService.removeProduct(itemCode);
  }

  closeCompareTray(): void {
    this.compareService.clearAll();
  }

  clearAllSelected(): void {
    this.compareService.clearAll();
    this.showSelectedList = false;
  }

  get maxProducts(): number {
    return this.compareService.maxProducts;
  }

  goToCompare(): void {
    if (this.compareProducts.length < 2) {
      return;
    }
    this.router.navigate(['/compare']);
  }

  viewSelected(): void {
    if (!this.compareProducts.length) {
      return;
    }
    this.showSelectedList = true;
  }

  closeSelectedList(): void {
    this.showSelectedList = false;
  }

  productLabel(product: any): string {
    const info = product?.General_Information || {};
    const description = String(info.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return info.searchKey1 || description || info.item || '';
  }

  productPrice(product: any): string {
    const info = product?.General_Information || {};
    const raw = info.price;
    if (raw == null || raw === '') {
      return '';
    }
    const numeric = Number(raw);
    const amount = Number.isFinite(numeric)
      ? new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(numeric)
      : String(raw);
    const symbol = formatProductCardCurrencyDisplayUnit(info.currency);
    if (!symbol) {
      return amount;
    }
    if (symbol === '€') {
      return `${amount}${symbol}`;
    }
    if (symbol === '$') {
      return `${symbol}${amount}`;
    }
    return `${amount} ${symbol}`;
  }

  goToProductList(): void {
    this.router.navigate(['/customer/CFProduct/product-view']);
  }

  onSlotImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img && !img.src.includes('assets/images/default.jpg')) {
      img.src = 'assets/images/default.jpg';
    }
  }

  private onNavigation(url: string): void {
    const nextIsProductPage = this.matchesProductCardPage(url);
    if (this.isProductCardPage && !nextIsProductPage) {
      this.leftProductPages = true;
    }
    if (nextIsProductPage && this.leftProductPages) {
      this.leftProductPages = false;
      this.compareService.clearAll();
    }
    this.showSelectedList = false;
    this.updateRoute(url);
    this.updateCompareTrayVisibility();
  }

  private updateRoute(url: string): void {
    this.isProductCardPage = this.matchesProductCardPage(url);
  }

  private updateCompareTrayVisibility(): void {
    this.showCompareTray =
      this.isCustomerLoggedIn &&
      this.compareButtonOnCard &&
      this.isProductCardPage &&
      this.compareProducts.length > 0;
  }

  /** True when at least one catalog card layout actually renders the compare button. */
  private syncCompareButtonVisibility(): void {
    const layouts: ProductCardLayoutType[] = ['vertical', 'horizontal', 'line'];
    const configs = layouts
      .map((layout) => this.productCardDisplayConfig.getSnapshot(layout))
      .filter((config) => !!config);
    this.compareButtonOnCard = configs.some((config) =>
      isProductCardElementVisible(config, 'compareButtonVisible')
    );
  }

  /**
   * Catalog screens whose product cards include the compare action.
   * Public home and the public product list do not render that button.
   */
  private matchesProductCardPage(url: string): boolean {
    const path = (url || '').split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
    return (
      path.startsWith('/sub-category/') ||
      path === '/customer/CFProduct/all' ||
      path === '/customer/CFProduct/product-view' ||
      path === '/customer/CFProduct/product-news' ||
      path === '/customer/CFDashboard/dashboard' ||
      path.startsWith('/customer/CFDashboard/detail-product/') ||
      path.startsWith('/customer/favorite-products/')
    );
  }
}
